import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container } from "react-bootstrap";
import { ArrowRight, User } from "lucide-react";
import { pressReleases } from "./pressReleasesData";
import intellsenseContent from "./content/comsense-technologies-unveils-intellsense-a-self-service-business-intelligence-and-reporting-tool";
import martechAwardsContent from "./content/comsense-technologies-wins-three-prestigious-martech-awards-for-customer-experience-and-marketing-innovation";

const pressReleasesContent = {
  ...intellsenseContent,
  ...martechAwardsContent,
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Component to render content sections from JSON
const ContentRenderer = ({ sections }) => {
  return (
    <>
      {sections.map((section, index) => {
        switch (section.type) {
          case "heading":
            const HeadingTag = `h${section.level}`;
            return (
              <HeadingTag key={index} className="press-release-content-heading">
                {section.content}
              </HeadingTag>
            );
          case "paragraph":
            // Check if content contains HTML tags
            const hasHTML = /<[^>]*>/.test(section.content);
            return hasHTML ? (
              <p
                key={index}
                className="press-release-content-paragraph"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            ) : (
              <p key={index} className="press-release-content-paragraph">
                {section.content}
              </p>
            );
          case "list":
            return (
              <ul key={index} className="press-release-content-list">
                {section.items.map((item, itemIndex) => {
                  if (typeof item === "string") {
                    const itemHasHTML = /<[^>]*>/.test(item);
                    return itemHasHTML ? (
                      <li
                        key={itemIndex}
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    ) : (
                      <li key={itemIndex}>{item}</li>
                    );
                  }
                  return (
                    <li key={itemIndex}>
                      <strong>{item.title}:</strong> {item.description}
                    </li>
                  );
                })}
              </ul>
            );
          case "blockquote":
            const blockquoteHasHTML = /<[^>]*>/.test(section.content);
            return (
              <blockquote
                key={index}
                className="press-release-content-blockquote"
              >
                <p>
                  {blockquoteHasHTML ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  ) : (
                    section.content
                  )}
                </p>
                {section.author && (
                  <footer>
                    <strong>{section.author}</strong>
                    {section.title && `, ${section.title}`}
                  </footer>
                )}
              </blockquote>
            );
          case "image":
            return (
              <figure key={index} className="press-release-content-image">
                <img src={section.src} alt={section.alt || ""} />
                {section.caption && (
                  <figcaption>{section.caption}</figcaption>
                )}
              </figure>
            );
          case "contact":
            return (
              <div key={index} className="press-release-contact-info">
                <p>
                  <strong>{section.company}</strong>
                  <br />
                  Email: <a href={`mailto:${section.email}`}>{section.email}</a>
                  <br />
                  Phone: {section.phone}
                </p>
              </div>
            );
          default:
            return null;
        }
      })}
    </>
  );
};

const PressReleaseDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const release = pressReleases.find(
    (item) =>
      item.slug === slug ||
      slugify(item.title) === slug ||
      String(item.id) === slug,
  );

  useEffect(() => {
    if (release && String(release.id) === slug) {
      // Redirect old numeric URL to slug URL
      navigate(`/press-releases/${release.slug}`, { replace: true });
    }
  }, [release, slug, navigate]);

  const content = useMemo(() => {
    if (!release) return null;
    const key = release.slug || slugify(release.title);
    return pressReleasesContent[key] || null;
  }, [release]);

  if (!release) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <h2>Press Release not found</h2>
          <p>
            The press release you are looking for does not exist or has been
            removed.
          </p>
          <Link to="/press-releases" className="btn btn-dark">
            Back to Press Releases
          </Link>
        </div>
      </Container>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');
        .press-release-details {
          font-family: 'Outfit', sans-serif;
          background-color: #f8fafc;
          padding: 40px 0;
        }

        .press-release-details * {
          font-family: 'Outfit', sans-serif;
          
        }

        .press-release-hero-detail {
          background: linear-gradient(135deg, #e2f1fe 0%, #f0f9ff 100%);
          position: relative;
          overflow: hidden;
          padding: 80px 0 80px;
        }

        .press-release-hero-detail h1 {
          font-weight: 800;
          font-size: 42px;
          color: #0f172a;
          margin-bottom: 20px;
          line-height: 1.3;
        }

        .press-release-meta-info {
          display: flex;
          gap: 30px;
          flex-wrap: wrap;
          padding-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
          font-size: 14px;
          color: #64748b;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .meta-item strong {
          color: #0f172a;
        }

        // .press-release-body-wrapper {
        //   padding: 60px 0;
        // }

        .press-release-body {
          line-height: 1.8;
          color: #0f172a;
        }

        .press-release-body h2 {
          font-size: 28px;
          font-weight: 700;
          margin-top: 40px;
          margin-bottom: 20px;
          color: #0f172a;
        }

        .press-release-body h3 {
          font-size: 22px;
          font-weight: 700;
          margin-top: 30px;
          margin-bottom: 15px;
          color: #0f172a;
        }

        .press-release-body p {
          margin-bottom: 20px;
          font-size: 16px;
          color: #334155;
        }

        .press-release-body ul,
        .press-release-body ol {
          margin-bottom: 20px;
          padding-left: 30px;
        }

        .press-release-body li {
          margin-bottom: 10px;
          font-size: 16px;
          color: #334155;
        }

        .press-release-body blockquote {
          border-left: 4px solid #e00;
          padding: 20px;
          margin: 30px 0;
          background: #f0f9ff;
          font-style: italic;
          color: #0f172a;
        }

        .press-release-body code {
          background: #f1f5f9;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 14px;
        }

        .press-release-body pre {
          background: #1e293b;
          color: #e2e8f0;
          padding: 20px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 20px 0;
        }

        .press-release-body img {
          max-width: 100%;
          height: auto;
          // margin: 30px 0;
          border-radius: 8px;
        }

        .press-release-featured-image {
          width: 100%;
          max-height: 400px;
          object-fit: cover;
          border-radius: 10px;
          margin-bottom: 40px;
        }

        .press-release-back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #e00;
          text-decoration: none;
          font-weight: 600;
          margin-bottom: 15px;
          transition: all 0.3s ease;
        }

        .press-release-back-link:hover {
          gap: 12px;
          color: #0056b3;
        }

        .press-release-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 40px 0;
          margin-top: 60px;
          border-top: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          gap: 20px;
        }

        .nav-prev,
        .nav-next {
          flex: 1;
          padding: 20px;
          border-radius: 8px;
          background: #f8fafc;
          transition: all 0.3s ease;
          text-decoration: none;
          color: #0f172a;
        }

        .nav-prev:hover,
        .nav-next:hover {
          background: #e0f2fe;
          transform: translateX(-5px);
        }

        .nav-next:hover {
          transform: translateX(5px);
        }

        .nav-label {
          font-size: 12px;
          color: #64748b;
          margin-bottom: 5px;
        }

        .nav-title {
          font-weight: 700;
          margin-bottom: 5px;
        }

        /* Content Renderer Styles */
        .press-release-content-heading {
          font-weight: 700;
          margin-top: 30px;
          margin-bottom: 15px;
          color: #0f172a;
        }

        .press-release-content-heading:first-child {
          margin-top: 0;
        }

        .press-release-content-paragraph {
          margin-bottom: 20px;
          font-size: 16px;
          color: #334155;
          line-height: 1.8;
        }

        .press-release-content-list {
          margin-bottom: 20px;
          padding-left: 30px;
          list-style: disc;
        }

        .press-release-content-list li {
          margin-bottom: 12px;
          font-size: 16px;
          color: #334155;
          line-height: 1.6;
        }

        .press-release-content-image {
          // margin: 30px 0;
        }

        .press-release-content-image img {
          width: 100%;
          height: auto;
          object-fit: contain;
          border-radius: 8px;
        }

        .press-release-content-image figcaption {
          margin-top: 10px;
          font-size: 14px;
          color: #64748b;
          text-align: center;
        }

        .press-release-content-blockquote {
          border-left: 4px solid #007bff;
          padding: 20px;
          margin: 30px 0;
          background: #f0f9ff;
          border-radius: 4px;
        }

        .press-release-content-blockquote p {
          font-style: italic;
        margin-bottom: 0px;
          color: #0f172a;
        }

        .press-release-content-blockquote footer {
          font-style: normal;
          color: #64748b;
          font-size: 14px;
        }

        .press-release-contact-info {
          background: #f8fafc;
          padding: 20px;
          border-radius: 8px;
          margin-top: 30px;
          border-left: 4px solid #007bff;
        }

        .press-release-contact-info p {
          margin: 0;
          font-size: 16px;
          color: #334155;
        }

        .press-release-contact-info a {
          color: #007bff;
          text-decoration: none;
        }

        .press-release-contact-info a:hover {
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .press-release-hero-detail h1 {
            font-size: 32px;
          }

          .press-release-meta-info {
            flex-direction: column;
            gap: 15px;
          }

          .press-release-navigation {
            flex-direction: column;
          }

          .nav-prev,
          .nav-next {
            width: 100%;
          }
        }
      `}</style>

      <section className="press-release-hero-detail">
        <Container>
          <div className="">
            <Link to="/press-releases" className="press-release-back-link">
              <ArrowRight size={16} style={{ transform: "rotate(180deg)" }} />
              Back to Press Releases
            </Link>

            <h1>{release.title}</h1>

            <div className="press-release-meta-info">
              {/* <div className="meta-item">
                <Calendar size={16} />
                <strong>Date:</strong>
                <span>{release.date}</span>
              </div> */}
              <div className="meta-item">
                <User size={16} />
                {/* <strong>Author:</strong> */}
                <span>By {release.author}</span>
              </div>
              {/* <div className="meta-item">
                <Tag size={16} />
                <strong>Category:</strong>
                <span>{release.category}</span>
              </div> */}
            </div>
          </div>
        </Container>
      </section>

      <div className="press-release-details">
        <Container>
          {release.image && !release.hideFeaturedImage && (
            <img
              src={release.image}
              alt={release.title}
              className="press-release-featured-image"
            />
          )}

          <div className="press-release-body-wrapper">
            <div className="press-release-body">
              {content && content.sections ? (
                <ContentRenderer sections={content.sections} />
              ) : (
                <div>
                  <h2>Coming Soon</h2>
                  <p>
                    The full press release content is being prepared. Please
                    check back soon for the complete details.
                  </p>
                  <p>
                    <strong>Summary:</strong> {release.excerpt}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};

export default PressReleaseDetails;
